import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Simulación del rebalanceo, estilo selector de liquidez profesional (Uniswap V3).
 *
 * EJE = PRECIO. Las barras son la liquidez concentrada: altas dentro del rango activo, un stub fuera
 * (fuera no cobras). El rango se marca con DOS LÍNEAS VERTICALES (mínimo y máximo) y su % de
 * amplitud. El precio (punto) deriva; cuando se sale del rango, aparece un rango NUEVO centrado en él
 * y el anterior queda dibujado en tenue — la historia de reajustes siempre a la vista, del inicial
 * en adelante.
 *
 * Determinista y en bucle; se apaga con `prefers-reduced-motion` mostrando el estado final.
 */

const N = 150;
const HW = 7;                        // medio-ancho del rango (unidades de precio)
const W = 640, H = 300;
const PL = 20, PR = 20, PT = 46, PB = 30;
const BUCKETS = 48;

type Rango = { ini: number; fin: number; lo: number; hi: number };

function simular() {
  const precio: number[] = [];
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    precio.push(
      100
      + 17 * Math.sin(t * Math.PI * 2 * 1.15 + 0.5)
      + 7.5 * Math.sin(t * Math.PI * 2 * 2.7 + 1.1)
      - 7 * Math.cos(t * Math.PI * 2 * 0.5),
    );
  }
  const rangos: Rango[] = [];
  let lo = precio[0]! - HW, hi = precio[0]! + HW, ini = 0;
  for (let i = 1; i <= N; i++) {
    if (precio[i]! > hi || precio[i]! < lo) {
      rangos.push({ ini, fin: i, lo, hi });
      lo = precio[i]! - HW; hi = precio[i]! + HW; ini = i;
    }
  }
  rangos.push({ ini, fin: N, lo, hi });
  const vals = [...precio, ...rangos.flatMap((r) => [r.lo, r.hi])];
  const pmin = Math.min(...vals) - 2, pmax = Math.max(...vals) + 2;
  return { precio, rangos, pmin, pmax };
}

const amp = (r: Rango) => Math.round(((r.hi - r.lo) / 2) / ((r.hi + r.lo) / 2) * 100);

export function SimulacionRango() {
  const { precio, rangos, pmin, pmax } = useMemo(simular, []);
  const [head, setHead] = useState(N);
  const raf = useRef(0);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setHead(N); return; }
    const DUR = 9000, HOLD = 1600;
    let t0: number | null = null;
    const loop = (ts: number) => {
      if (t0 === null) t0 = ts;
      const e = (ts - t0) % (DUR + HOLD);
      setHead(e < DUR ? (e / DUR) * N : N);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  const X = (p: number) => PL + (p - pmin) / (pmax - pmin) * (W - PL - PR);
  const base = H - PB;                       // línea de suelo de las barras
  const hMax = H - PT - PB;

  // precio actual (interpolado) y rango activo en el playhead
  const nEnt = Math.min(N - 1, Math.floor(head));
  const f = head - nEnt;
  const pAhora = precio[nEnt]! + (precio[nEnt + 1]! - precio[nEnt]!) * (head >= N ? 0 : f);
  const cur = rangos.find((r) => head < r.fin) ?? rangos[rangos.length - 1]!;
  const center = (cur.lo + cur.hi) / 2, hw = (cur.hi - cur.lo) / 2;

  const visibles = rangos.filter((r) => r.ini <= head);
  const barW = (W - PL - PR) / BUCKETS * 0.72;

  return (
    <figure className="sim-rango">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Simulación del rango de liquidez que se reajusta siguiendo al precio">
        {/* rejilla */}
        {[0.33, 0.66, 1].map((g) => (
          <line key={g} className="sim-grid" x1={PL} x2={W - PR} y1={base - g * hMax} y2={base - g * hMax} />
        ))}

        {/* barras de liquidez: altas dentro del rango activo, stub fuera */}
        {Array.from({ length: BUCKETS }, (_, b) => {
          const px = pmin + (b + 0.5) / BUCKETS * (pmax - pmin);
          const dentro = px >= cur.lo && px <= cur.hi;
          const frac = dentro ? 0.38 + 0.62 * Math.cos((px - center) / hw * Math.PI / 2) ** 2 : 0.1;
          const bh = Math.max(2, frac * hMax);
          return (
            <rect key={b} className={dentro ? "sim-bar" : "sim-bar-out"}
              x={X(px) - barW / 2} y={base - bh} width={barW} height={bh} rx={2} />
          );
        })}

        {/* rangos ANTERIORES: par de líneas verticales tenues + su amplitud */}
        {visibles.filter((r) => head >= r.fin).map((r, k) => (
          <g key={`old${k}`} className="sim-old">
            <line x1={X(r.lo)} x2={X(r.lo)} y1={PT - 6} y2={base} />
            <line x1={X(r.hi)} x2={X(r.hi)} y1={PT - 6} y2={base} />
            <text x={(X(r.lo) + X(r.hi)) / 2} y={PT - 10} textAnchor="middle">
              {k === 0 ? `inicial ±${amp(r)}%` : `±${amp(r)}%`}
            </text>
          </g>
        ))}

        {/* rango ACTUAL: líneas verticales sólidas + etiqueta con % de amplitud */}
        <g className="sim-now">
          <line x1={X(cur.lo)} x2={X(cur.lo)} y1={PT - 8} y2={base} />
          <line x1={X(cur.hi)} x2={X(cur.hi)} y1={PT - 8} y2={base} />
          <line className="sim-now-top" x1={X(cur.lo)} x2={X(cur.hi)} y1={PT - 8} y2={PT - 8} />
          <text x={(X(cur.lo) + X(cur.hi)) / 2} y={PT - 16} textAnchor="middle">
            {rangos.indexOf(cur) === 0 ? "rango inicial" : "rango actual"} · ±{amp(cur)}%
          </text>
        </g>

        {/* PRECIO: línea vertical + punto sobre el suelo */}
        <line className="sim-price" x1={X(pAhora)} x2={X(pAhora)} y1={PT - 2} y2={base} />
        <circle className="sim-dot-halo" cx={X(pAhora)} cy={base} r={9} />
        <circle className="sim-dot" cx={X(pAhora)} cy={base} r={4.5} />
      </svg>

      <figcaption className="sim-leyenda">
        <span><i className="c-now" />rango activo (cobras)</span>
        <span><i className="c-out" />fuera (no cobras)</span>
        <span><i className="c-old" />reajustes anteriores</span>
        <span><i className="c-price" />precio</span>
      </figcaption>
    </figure>
  );
}
